/**
 * Declarative QR frame specs.
 *
 * A spec describes the framed artwork in absolute pixels for a given QR size:
 * shapes painted below the QR, the QR position, and shapes painted above it.
 * Two thin renderers (canvas + SVG) consume the same spec so raster and vector
 * exports stay identical.
 */

export const FRAME_TYPES = [
    'none',
    'simple',
    'rounded',
    'banner',
    'rounded-banner',
    'badge',
    'double',
    'pill',
    'top-banner',
    'bold',
    'thin',
    'dashed',
    'corners',
    'banner-both',
    'ribbon',
    'shadow',
    'speech',
    'speech-right',
    'speech-center',
    'speech-outline',
    'thought',
];

const SANS = 'Arial, Helvetica, -apple-system, BlinkMacSystemFont, sans-serif';

function rect(x, y, w, h, extra = {}) {
    return { kind: 'rect', x, y, w, h, ...extra };
}

function line(x1, y1, x2, y2, extra = {}) {
    return { kind: 'line', x1, y1, x2, y2, ...extra };
}

function text(x, y, value, extra = {}) {
    return { kind: 'text', x, y, text: value, ...extra };
}

/** `points` is a flat [x0,y0,x1,y1,…] list, used for frame tails and brackets. */
function polygon(points, extra = {}) {
    return { kind: 'polygon', points, ...extra };
}

function circle(cx, cy, r, extra = {}) {
    return { kind: 'circle', cx, cy, r, ...extra };
}

/**
 * @param {string} frameType
 * @param {number} qrSize   rendered QR width/height in px
 * @param {object} opts     { label, borderColor, labelBgColor, labelTextColor, bgColor }
 * @returns {null|{width:number,height:number,qrX:number,qrY:number,below:Array,above:Array}}
 */
export function buildFrameSpec(frameType, qrSize, opts) {
    if (!frameType || frameType === 'none') return null;

    const {
        label = 'Scan Me',
        borderColor = '#000000',
        labelBgColor = '#000000',
        labelTextColor = '#ffffff',
        bgColor = '#ffffff',
    } = opts || {};

    const qrW = qrSize;
    const qrH = qrSize;
    const PAD = Math.round(qrW * 0.055);
    const LABEL_H = Math.round(qrW * 0.145);
    const BORDER = Math.max(3, Math.round(qrW * 0.01));
    const fontSize = Math.round(LABEL_H * 0.44);
    const labelText = label || 'Scan Me';

    const W = qrW + PAD * 2;
    const H = qrH + PAD * 2 + LABEL_H;

    /**
     * Centred in the label band, allowing for the border that eats into its
     * bottom edge — without `bottomInset` a thick border leaves the text
     * looking pushed down against it.
     */
    const bottomLabel = (color, offsetY = 0, bottomInset = BORDER) => text(
        W / 2,
        qrH + PAD * 2 + offsetY + (LABEL_H - bottomInset) / 2,
        labelText,
        { fill: color, fontSize, weight: 'bold' },
    );

    switch (frameType) {
        case 'simple':
            return {
                width: W,
                height: H,
                qrX: PAD,
                qrY: PAD,
                below: [rect(0, 0, W, H, { fill: bgColor })],
                above: [
                    rect(BORDER / 2, BORDER / 2, W - BORDER, H - BORDER, { stroke: borderColor, lineWidth: BORDER }),
                    line(BORDER, qrH + PAD * 2, W - BORDER, qrH + PAD * 2, { stroke: borderColor, lineWidth: 1, alpha: 0.4 }),
                    bottomLabel(borderColor),
                ],
            };

        case 'rounded': {
            const R = Math.round(qrW * 0.04);
            return {
                width: W,
                height: H,
                qrX: PAD,
                qrY: PAD,
                below: [rect(0, 0, W, H, { fill: bgColor, rx: R })],
                above: [
                    rect(BORDER / 2, BORDER / 2, W - BORDER, H - BORDER, { stroke: borderColor, lineWidth: BORDER, rx: R }),
                    line(PAD, qrH + PAD * 2, W - PAD, qrH + PAD * 2, { stroke: borderColor, lineWidth: 1, alpha: 0.35 }),
                    bottomLabel(borderColor),
                ],
            };
        }

        case 'banner':
            return {
                width: W,
                height: H,
                qrX: PAD,
                qrY: PAD,
                below: [
                    rect(0, 0, W, qrH + PAD * 2, { fill: bgColor }),
                    rect(0, qrH + PAD * 2, W, LABEL_H, { fill: labelBgColor }),
                ],
                above: [
                    rect(BORDER / 2, BORDER / 2, W - BORDER, H - BORDER, { stroke: borderColor, lineWidth: BORDER }),
                    bottomLabel(labelTextColor),
                ],
            };

        case 'rounded-banner': {
            const R = Math.round(qrW * 0.05);
            return {
                width: W,
                height: H,
                qrX: PAD,
                qrY: PAD,
                below: [
                    rect(0, 0, W, H, { fill: labelBgColor, rx: R }),
                    rect(BORDER * 2, BORDER * 2, W - BORDER * 4, qrH + PAD * 2 - BORDER * 2, { fill: bgColor, rx: Math.max(0, R - 2) }),
                ],
                above: [bottomLabel(labelTextColor, 0, 0)],
            };
        }

        case 'badge': {
            const THICK = Math.max(6, Math.round(qrW * 0.02));
            const badgeH = qrH + PAD * 2 + THICK * 2 + LABEL_H;
            const R = Math.round(qrW * 0.03);
            return {
                width: W,
                height: badgeH,
                qrX: PAD,
                qrY: PAD + THICK * 2,
                below: [
                    rect(0, 0, W, badgeH, { fill: bgColor, rx: R }),
                    rect(THICK / 2, THICK / 2, W - THICK, badgeH - THICK, { stroke: borderColor, lineWidth: THICK, rx: R }),
                    rect(THICK / 2, THICK / 2, W - THICK, THICK * 2.5, { fill: borderColor }),
                ],
                above: [
                    line(THICK, qrH + PAD * 2 + THICK * 2, W - THICK, qrH + PAD * 2 + THICK * 2, { stroke: borderColor, lineWidth: 1, alpha: 0.3 }),
                    bottomLabel(borderColor, THICK * 2, THICK),
                ],
            };
        }

        case 'double': {
            const OUTER_B = Math.max(3, Math.round(qrW * 0.015));
            const INNER_B = Math.max(1, Math.round(qrW * 0.007));
            const GAP = Math.round(qrW * 0.025);
            return {
                width: W,
                height: H,
                qrX: PAD,
                qrY: PAD,
                below: [rect(0, 0, W, H, { fill: bgColor })],
                above: [
                    rect(OUTER_B / 2, OUTER_B / 2, W - OUTER_B, H - OUTER_B, { stroke: borderColor, lineWidth: OUTER_B }),
                    rect(OUTER_B + GAP, OUTER_B + GAP, W - (OUTER_B + GAP) * 2, H - (OUTER_B + GAP) * 2, { stroke: borderColor, lineWidth: INNER_B, alpha: 0.4 }),
                    bottomLabel(borderColor, 0, OUTER_B + GAP),
                ],
            };
        }

        case 'pill': {
            const R = Math.round(Math.min(W, H) * 0.35);
            return {
                width: W,
                height: H,
                qrX: PAD,
                qrY: PAD,
                below: [rect(0, 0, W, H, { fill: bgColor, rx: R })],
                above: [
                    rect(BORDER / 2, BORDER / 2, W - BORDER, H - BORDER, { stroke: borderColor, lineWidth: BORDER, rx: R }),
                    line(PAD * 1.5, qrH + PAD * 2, W - PAD * 1.5, qrH + PAD * 2, { stroke: borderColor, lineWidth: 1, alpha: 0.3 }),
                    bottomLabel(borderColor),
                ],
            };
        }

        case 'top-banner': {
            const TOP_H = Math.round(qrW * 0.145);
            const topH = qrH + PAD + PAD / 2 + TOP_H;
            const topFontSize = Math.round(TOP_H * 0.44);
            return {
                width: W,
                height: topH,
                qrX: PAD,
                qrY: TOP_H + PAD / 2,
                below: [
                    rect(0, 0, W, TOP_H, { fill: labelBgColor }),
                    rect(0, TOP_H, W, topH - TOP_H, { fill: bgColor }),
                ],
                above: [
                    rect(BORDER / 2, BORDER / 2, W - BORDER, topH - BORDER, { stroke: borderColor, lineWidth: BORDER }),
                    text(W / 2, TOP_H / 2, labelText, { fill: labelTextColor, fontSize: topFontSize, weight: 'bold' }),
                ],
            };
        }

        case 'bold': {
            const THICK = Math.max(6, Math.round(qrW * 0.035));
            return {
                width: W,
                height: H,
                qrX: PAD,
                qrY: PAD,
                below: [rect(0, 0, W, H, { fill: bgColor })],
                above: [
                    rect(THICK / 2, THICK / 2, W - THICK, H - THICK, { stroke: borderColor, lineWidth: THICK }),
                    bottomLabel(borderColor, 0, THICK),
                ],
            };
        }

        case 'thin': {
            const HAIR = Math.max(1, Math.round(qrW * 0.004));
            return {
                width: W,
                height: H,
                qrX: PAD,
                qrY: PAD,
                below: [rect(0, 0, W, H, { fill: bgColor })],
                above: [
                    rect(HAIR / 2, HAIR / 2, W - HAIR, H - HAIR, { stroke: borderColor, lineWidth: HAIR }),
                    bottomLabel(borderColor),
                ],
            };
        }

        case 'dashed': {
            const DASH = Math.round(qrW * 0.03);
            return {
                width: W,
                height: H,
                qrX: PAD,
                qrY: PAD,
                below: [rect(0, 0, W, H, { fill: bgColor })],
                above: [
                    rect(BORDER / 2, BORDER / 2, W - BORDER, H - BORDER, {
                        stroke: borderColor,
                        lineWidth: BORDER,
                        dash: [DASH, Math.round(DASH * 0.7)],
                    }),
                    bottomLabel(borderColor),
                ],
            };
        }

        // Four corner brackets instead of a closed border — the QR breathes and
        // the label still anchors the bottom.
        case 'corners': {
            const THICK = Math.max(4, Math.round(qrW * 0.022));
            const ARM = Math.round(qrW * 0.18);
            const o = THICK / 2;
            const bracket = (x, y, dx, dy) => [
                line(x, y, x + ARM * dx, y, { stroke: borderColor, lineWidth: THICK }),
                line(x, y, x, y + ARM * dy, { stroke: borderColor, lineWidth: THICK }),
            ];
            return {
                width: W,
                height: H,
                qrX: PAD,
                qrY: PAD,
                below: [rect(0, 0, W, H, { fill: bgColor })],
                above: [
                    ...bracket(o, o, 1, 1),
                    ...bracket(W - o, o, -1, 1),
                    ...bracket(o, H - o, 1, -1),
                    ...bracket(W - o, H - o, -1, -1),
                    bottomLabel(borderColor),
                ],
            };
        }

        // Solid bar above the code and the label bar below it.
        case 'banner-both': {
            const TOP_H = Math.round(LABEL_H * 0.42);
            const height = H + TOP_H;
            return {
                width: W,
                height,
                qrX: PAD,
                qrY: PAD + TOP_H,
                below: [
                    rect(0, 0, W, TOP_H, { fill: labelBgColor }),
                    rect(0, TOP_H, W, qrH + PAD * 2, { fill: bgColor }),
                    rect(0, qrH + PAD * 2 + TOP_H, W, LABEL_H, { fill: labelBgColor }),
                ],
                above: [
                    rect(BORDER / 2, BORDER / 2, W - BORDER, height - BORDER, { stroke: borderColor, lineWidth: BORDER }),
                    bottomLabel(labelTextColor, TOP_H),
                ],
            };
        }

        // Pill-shaped label sitting on the bottom border, like a stamp.
        case 'ribbon': {
            const R = Math.round(qrW * 0.045);
            const pillH = Math.round(LABEL_H * 0.82);
            const pillW = Math.round(W * 0.66);
            const pillY = qrH + PAD * 2 - pillH / 2 + Math.round(PAD * 0.35);
            return {
                width: W,
                height: H,
                qrX: PAD,
                qrY: PAD,
                below: [rect(0, 0, W, H, { fill: bgColor, rx: R })],
                above: [
                    rect(BORDER / 2, BORDER / 2, W - BORDER, H - BORDER, { stroke: borderColor, lineWidth: BORDER, rx: R }),
                    rect((W - pillW) / 2, pillY, pillW, pillH, { fill: labelBgColor, rx: pillH / 2 }),
                    text(W / 2, pillY + pillH / 2, labelText, { fill: labelTextColor, fontSize, weight: 'bold' }),
                ],
            };
        }

        // Offset block behind a flat card — a printed sticker look.
        case 'shadow': {
            const OFF = Math.round(qrW * 0.035);
            const R = Math.round(qrW * 0.035);
            return {
                width: W + OFF,
                height: H + OFF,
                qrX: PAD,
                qrY: PAD,
                below: [
                    rect(OFF, OFF, W, H, { fill: borderColor, rx: R }),
                    rect(0, 0, W, H, { fill: bgColor, rx: R }),
                ],
                above: [
                    rect(BORDER / 2, BORDER / 2, W - BORDER, H - BORDER, { stroke: borderColor, lineWidth: BORDER, rx: R }),
                    bottomLabel(borderColor),
                ],
            };
        }

        // Rounded card with a chat-bubble tail under the label. `speech-right`
        // and `speech-center` are the same bubble with the tail moved, so the
        // three read as one family in the picker.
        case 'speech':
        case 'speech-right':
        case 'speech-center': {
            const R = Math.round(qrW * 0.06);
            const TAIL = Math.round(qrW * 0.07);
            const mouth = TAIL * 1.5;
            // The tail leans away from its anchor for the side variants and is a
            // symmetric wedge when it hangs from the middle.
            let tail;
            if (frameType === 'speech-right') {
                const tailX = Math.round(W * 0.72);
                tail = [tailX, H - 1, tailX - mouth, H - 1, tailX, H + TAIL];
            } else if (frameType === 'speech-center') {
                tail = [W / 2 - mouth / 2, H - 1, W / 2 + mouth / 2, H - 1, W / 2, H + TAIL];
            } else {
                const tailX = Math.round(W * 0.28);
                tail = [tailX, H - 1, tailX + mouth, H - 1, tailX, H + TAIL];
            }
            return {
                width: W,
                height: H + TAIL,
                qrX: PAD,
                qrY: PAD,
                below: [
                    rect(0, 0, W, H, { fill: labelBgColor, rx: R }),
                    polygon(tail, { fill: labelBgColor }),
                    rect(BORDER * 2, BORDER * 2, W - BORDER * 4, qrH + PAD * 2 - BORDER * 2, {
                        fill: bgColor,
                        rx: Math.max(0, R - BORDER * 2),
                    }),
                ],
                above: [bottomLabel(labelTextColor, 0, 0)],
            };
        }

        // Outlined bubble: the border runs round the card, then the tail's fill
        // knocks out the stroke across its mouth so the two read as one shape.
        case 'speech-outline': {
            const R = Math.round(qrW * 0.06);
            const TAIL = Math.round(qrW * 0.07);
            const mouth = Math.round(TAIL * 1.5);
            const tailX = Math.round(W * 0.28);
            return {
                width: W,
                height: H + TAIL,
                qrX: PAD,
                qrY: PAD,
                below: [
                    rect(0, 0, W, H, { fill: bgColor, rx: R }),
                    polygon([tailX, H - BORDER, tailX + mouth, H - BORDER, tailX, H + TAIL], { fill: bgColor }),
                ],
                above: [
                    rect(BORDER / 2, BORDER / 2, W - BORDER, H - BORDER, { stroke: borderColor, lineWidth: BORDER, rx: R }),
                    rect(tailX + BORDER / 2, H - BORDER * 1.5, mouth - BORDER, BORDER * 2, { fill: bgColor }),
                    line(tailX, H - BORDER * 1.5, tailX, H + TAIL, { stroke: borderColor, lineWidth: BORDER }),
                    line(tailX, H + TAIL, tailX + mouth, H - BORDER * 1.5, { stroke: borderColor, lineWidth: BORDER }),
                    line(PAD, qrH + PAD * 2, W - PAD, qrH + PAD * 2, { stroke: borderColor, lineWidth: 1, alpha: 0.35 }),
                    bottomLabel(borderColor),
                ],
            };
        }

        // Thought bubble: the same filled card, trailed by two shrinking dots.
        case 'thought': {
            const R = Math.round(qrW * 0.09);
            const BIG = Math.round(qrW * 0.045);
            const SMALL = Math.round(qrW * 0.026);
            const cx = Math.round(W * 0.3);
            const bigY = H + BIG * 0.7;
            const smallY = bigY + BIG + SMALL * 1.6;
            return {
                width: W,
                height: Math.round(smallY + SMALL + BORDER),
                qrX: PAD,
                qrY: PAD,
                below: [
                    rect(0, 0, W, H, { fill: labelBgColor, rx: R }),
                    circle(cx, bigY, BIG, { fill: labelBgColor }),
                    circle(cx - BIG, smallY, SMALL, { fill: labelBgColor }),
                    rect(BORDER * 2, BORDER * 2, W - BORDER * 4, qrH + PAD * 2 - BORDER * 2, {
                        fill: bgColor,
                        rx: Math.max(0, R - BORDER * 2),
                    }),
                ],
                above: [bottomLabel(labelTextColor, 0, 0)],
            };
        }

        default:
            return null;
    }
}

function roundRectPath(ctx, x, y, w, h, r) {
    const safeR = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + safeR, y);
    ctx.lineTo(x + w - safeR, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + safeR);
    ctx.lineTo(x + w, y + h - safeR);
    ctx.quadraticCurveTo(x + w, y + h, x + w - safeR, y + h);
    ctx.lineTo(x + safeR, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - safeR);
    ctx.lineTo(x, y + safeR);
    ctx.quadraticCurveTo(x, y, x + safeR, y);
    ctx.closePath();
}

function paintShape(ctx, shape) {
    ctx.save();
    ctx.globalAlpha = shape.alpha ?? 1;
    if (shape.dash) ctx.setLineDash(shape.dash);

    if (shape.kind === 'circle') {
        ctx.beginPath();
        ctx.arc(shape.cx, shape.cy, shape.r, 0, Math.PI * 2);
        if (shape.fill) { ctx.fillStyle = shape.fill; ctx.fill(); }
        if (shape.stroke) { ctx.strokeStyle = shape.stroke; ctx.lineWidth = shape.lineWidth || 1; ctx.stroke(); }
    } else if (shape.kind === 'polygon') {
        const [x0, y0, ...rest] = shape.points;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        for (let i = 0; i < rest.length; i += 2) ctx.lineTo(rest[i], rest[i + 1]);
        ctx.closePath();
        if (shape.fill) { ctx.fillStyle = shape.fill; ctx.fill(); }
        if (shape.stroke) { ctx.strokeStyle = shape.stroke; ctx.lineWidth = shape.lineWidth || 1; ctx.stroke(); }
    } else if (shape.kind === 'rect') {
        if (shape.rx) {
            roundRectPath(ctx, shape.x, shape.y, shape.w, shape.h, shape.rx);
            if (shape.fill) { ctx.fillStyle = shape.fill; ctx.fill(); }
            if (shape.stroke) { ctx.strokeStyle = shape.stroke; ctx.lineWidth = shape.lineWidth || 1; ctx.stroke(); }
        } else if (shape.fill) {
            ctx.fillStyle = shape.fill;
            ctx.fillRect(shape.x, shape.y, shape.w, shape.h);
        } else if (shape.stroke) {
            ctx.strokeStyle = shape.stroke;
            ctx.lineWidth = shape.lineWidth || 1;
            ctx.strokeRect(shape.x, shape.y, shape.w, shape.h);
        }
    } else if (shape.kind === 'line') {
        ctx.strokeStyle = shape.stroke;
        ctx.lineWidth = shape.lineWidth || 1;
        ctx.beginPath();
        ctx.moveTo(shape.x1, shape.y1);
        ctx.lineTo(shape.x2, shape.y2);
        ctx.stroke();
    } else if (shape.kind === 'text') {
        ctx.fillStyle = shape.fill;
        ctx.font = `${shape.weight || 'bold'} ${shape.fontSize}px ${SANS}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(shape.text, shape.x, shape.y);
    }

    ctx.restore();
}

/** Draw a framed QR onto `outCanvas`, sizing it to the spec. */
export function renderFramedCanvas(spec, qrImage, outCanvas) {
    outCanvas.width = spec.width;
    outCanvas.height = spec.height;
    const ctx = outCanvas.getContext('2d');
    ctx.clearRect(0, 0, spec.width, spec.height);
    spec.below.forEach((shape) => paintShape(ctx, shape));
    ctx.drawImage(qrImage, spec.qrX, spec.qrY);
    spec.above.forEach((shape) => paintShape(ctx, shape));
    return outCanvas;
}

function escapeXml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function shapeToSvg(shape) {
    const alpha = shape.alpha != null && shape.alpha !== 1 ? ` opacity="${shape.alpha}"` : '';
    const dash = shape.dash ? ` stroke-dasharray="${shape.dash.join(' ')}"` : '';

    if (shape.kind === 'circle') {
        const paint = shape.fill
            ? ` fill="${shape.fill}"`
            : ` fill="none" stroke="${shape.stroke}" stroke-width="${shape.lineWidth || 1}"`;
        return `<circle cx="${shape.cx}" cy="${shape.cy}" r="${shape.r}"${paint}${alpha}/>`;
    }

    if (shape.kind === 'polygon') {
        const pts = [];
        for (let i = 0; i < shape.points.length; i += 2) pts.push(`${shape.points[i]},${shape.points[i + 1]}`);
        const paint = shape.fill
            ? ` fill="${shape.fill}"`
            : ` fill="none" stroke="${shape.stroke}" stroke-width="${shape.lineWidth || 1}"`;
        return `<polygon points="${pts.join(' ')}"${paint}${alpha}/>`;
    }

    if (shape.kind === 'rect') {
        const rx = shape.rx ? ` rx="${shape.rx}"` : '';
        // Canvas strokes straddle the path, so SVG uses the same inset geometry.
        const paint = shape.fill
            ? ` fill="${shape.fill}"`
            : ` fill="none" stroke="${shape.stroke}" stroke-width="${shape.lineWidth || 1}"`;
        return `<rect x="${shape.x}" y="${shape.y}" width="${shape.w}" height="${shape.h}"${rx}${paint}${dash}${alpha}/>`;
    }

    if (shape.kind === 'line') {
        return `<line x1="${shape.x1}" y1="${shape.y1}" x2="${shape.x2}" y2="${shape.y2}" stroke="${shape.stroke}" stroke-width="${shape.lineWidth || 1}"${dash}${alpha}/>`;
    }

    if (shape.kind === 'text') {
        return `<text x="${shape.x}" y="${shape.y}" fill="${shape.fill}" font-family="${SANS}" font-size="${shape.fontSize}" font-weight="${shape.weight || 'bold'}" text-anchor="middle" dominant-baseline="central">${escapeXml(shape.text)}</text>`;
    }

    return '';
}

/** Wrap the rendered QR SVG inside the frame artwork, keeping it vector. */
export function renderFramedSvg(spec, qrSvgMarkup) {
    const inner = qrSvgMarkup.replace(/^[\s\S]*?<svg[^>]*>/i, '').replace(/<\/svg>\s*$/i, '');
    return [
        `<svg xmlns="http://www.w3.org/2000/svg" width="${spec.width}" height="${spec.height}" viewBox="0 0 ${spec.width} ${spec.height}">`,
        spec.below.map(shapeToSvg).join(''),
        `<g transform="translate(${spec.qrX} ${spec.qrY})">${inner}</g>`,
        spec.above.map(shapeToSvg).join(''),
        '</svg>',
    ].join('');
}
